<script setup lang="ts">
  import {
    computed,
    nextTick,
    onActivated,
    onBeforeUnmount,
    onMounted,
    reactive,
    ref,
    watch,
  } from 'vue';
  import {
    actions as actionTypes,
    useUserComponent,
    useHelpers,
    createComponentScope,
    Resource,
    uniqueID,
  } from '@integro/core';
  import type { IAppHelpers } from '@integro/core';
  import * as _ from 'lodash';

  defineOptions({
    name: 'CreateMeterReadingsParameters',
  });

  const componentMetadata = {
    kind: 'page',
  };

  const emit = defineEmits(['reload', 'close', 'save']);

  const props = defineProps({
    labelAlign: {
      type: String,
      required: false,
      default: 'right',
    },
    modal: {
      type: Boolean,
      required: false,
      default: false,
    },
    modalName: {
      type: String,
      required: false,
    },
  });

  const {
    $app,
    $fn,
    $i18n,
    $store,
    $config,
    $imodal,
    $notification,
    $tabs,
    $route,
    $router,
    gettext,
    pgettext,
    ngettext,
    npgettext,
    schemaDefaults,
    getSchema,
    searchDataSource,
    randomElementId,
    datetime,
    date,
    time,
    number,
    money,
    distance,
    username,
    filesize,
  } = useHelpers();

  const params = ref({
    notes: null,
    date_override: null,
  });
  const ux = ref([]);
  const b_7315092382398562_noShow = computed(() => false);
  const b_7315092382398562_modalBindings = computed(() => ({
    size: 'md',
  }));

  const $data = computed(() => ({
    params: params.value,
    ux: ux.value,
    b_7315092382398562_noShow: b_7315092382398562_noShow.value,
    b_7315092382398562_modalBindings: b_7315092382398562_modalBindings.value,
  }));

  const {
    loading,
    error,
    loaded,
    reloading,
    showLoadingIndicator,
    loadDataThrottled,
    friendlyName,
    loadData,
    touchLastLoaded,
    handleError,
    setData,
    forms,
    registerForm,
    unregisterForm,
    canLoadData,
    isModalComponent,
    modalName,
    pageDocument: _pageDocument,
    routeTab,
    ux: _ux,
    componentMetadata: _componentMetadata,
    $uid,
    lastLoaded,
  } = useUserComponent(props, {
    metadata: componentMetadata,
    dataSpecs: [
      {
        name: 'params',
        cache: { type: 'local', key: 'CreateMeterReadingsParameters' },
      },
      { name: 'ux' },
      { name: 'b_7315092382398562_noShow', nodeId: '7315092382398562' },
      { name: 'b_7315092382398562_modalBindings', nodeId: '7315092382398562' },
    ],
    loaders: {},
    ux: {},
    dataRefs: {
      params,
      ux,
      b_7315092382398562_noShow,
      b_7315092382398562_modalBindings,
    },
  });

  const $component = createComponentScope(
    {
      $app,
      $fn,
      $i18n,
      $store,
      $imodal,
      $notification,
      $tabs,
      $router,
    } as IAppHelpers,
    {
      emit,
      forms,
      error,
      isModalComponent,
      modalName,
      componentMetadata,
      loadData,
      registerForm,
      unregisterForm,
      setData,
      touchLastLoaded,
      handleError,
    }
  );

  const pageDocument = computed(() => {
    return {
      title: $fn.value.pgettext('CreateMeterReadings', 'Create Meter Readings'),
    };
  });
  const documentTitle = computed(() => {
    return pageDocument.value
      ? Array.isArray(pageDocument.value.title)
        ? pageDocument.value.title.filter(Boolean).join(' ')
        : pageDocument.value.title
      : null;
  });

  watch(
    () => props.labelAlign,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('labelAlign');
    }
  );
  watch(
    () => props.modal,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('modal');
    }
  );
  watch(
    () => props.modalName,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('modalName');
    }
  );

  watch(
    pageDocument,
    (newDoc) => {
      if (newDoc) {
        _pageDocument.value = newDoc;
      }
    },
    { immediate: true }
  );

  const act_7315092382398562_closeModal_xdn23u09w_getActionArgs = async (
    $event
  ) => {
    return {
      name: props.modalName,
      result: {
        close: true,
        cancel: true,
      },
    };
  };
  const act_1040167445267876_closeComponent_4fwyv3thz_getActionArgs = async (
    $event
  ) => {
    return {
      result: {
        close: true,
        ok: true,
        value: params.value,
      },
    };
  };

  const act_7315092382398562_closeModal_xdn23u09w =
    new actionTypes.CloseModalAction({
      actionArgs: act_7315092382398562_closeModal_xdn23u09w_getActionArgs,
    });
  const inst_evh_7315092382398562_close_46gzapj9q =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_closeModal_xdn23u09w,
      event: 'close',
      displayName: 'closeModal',
    });
  const inst_evh_7315092382398562_close = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_close_46gzapj9q],
    event: 'close',
  });
  const act_7315092382398562_reloadComponentData_082so7qpz =
    new actionTypes.ReloadComponentDataAction({});
  const inst_evh_7315092382398562_reload_qwhpqg274 =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_reloadComponentData_082so7qpz,
      event: 'reload',
      displayName: 'reloadComponentData',
    });
  const inst_evh_7315092382398562_reload = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_reload_qwhpqg274],
    event: 'reload',
  });
  const act_8564662037462133_closeComponent_oo8dcydpr =
    new actionTypes.CloseComponentAction({});
  const inst_evh_8564662037462133_close_noxx6r7dg =
    new actionTypes.EventHandlerImpl({
      action: act_8564662037462133_closeComponent_oo8dcydpr,
      event: 'close',
      displayName: 'closeComponent',
    });
  const inst_evh_8564662037462133_close = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_8564662037462133_close_noxx6r7dg],
    event: 'close',
  });
  const act_1040167445267876_closeComponent_4fwyv3thz =
    new actionTypes.CloseComponentAction({
      actionArgs: act_1040167445267876_closeComponent_4fwyv3thz_getActionArgs,
      displayName: 'submit',
    });
  const inst_evh_1040167445267876_submit_8n20xbfa4 =
    new actionTypes.EventHandlerImpl({
      action: act_1040167445267876_closeComponent_4fwyv3thz,
      event: 'submit',
      displayName: 'submit',
    });
  const inst_evh_1040167445267876_submit = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_1040167445267876_submit_8n20xbfa4],
    event: 'submit',
  });
  const act_2434514588044777_closeComponent_eod7ssry0 =
    new actionTypes.CloseComponentAction({});
  const inst_evh_2434514588044777_clickPreventStop_6d6j0gyfo =
    new actionTypes.EventHandlerImpl({
      action: act_2434514588044777_closeComponent_eod7ssry0,
      event: 'click.prevent.stop',
      displayName: 'closeComponent',
    });
  const inst_evh_2434514588044777_clickPreventStop =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_2434514588044777_clickPreventStop_6d6j0gyfo],
      event: 'click.prevent.stop',
    });

  const actions = {
    inst_evh_7315092382398562_close_46gzapj9q,
    inst_evh_7315092382398562_close,
    inst_evh_7315092382398562_reload_qwhpqg274,
    inst_evh_7315092382398562_reload,
    inst_evh_8564662037462133_close_noxx6r7dg,
    inst_evh_8564662037462133_close,
    inst_evh_1040167445267876_submit_8n20xbfa4,
    submit: inst_evh_1040167445267876_submit_8n20xbfa4,
    inst_evh_1040167445267876_submit,
    inst_evh_2434514588044777_clickPreventStop_6d6j0gyfo,
    inst_evh_2434514588044777_clickPreventStop,
  };

  const evh_7315092382398562_close_46gzapj9q = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_close_46gzapj9q.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_close = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_close.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload_qwhpqg274 = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload_qwhpqg274.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8564662037462133_close_noxx6r7dg = async (event, scope) => {
    return await actions.inst_evh_8564662037462133_close_noxx6r7dg.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8564662037462133_close = async (event, scope) => {
    return await actions.inst_evh_8564662037462133_close.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1040167445267876_submit_8n20xbfa4 = async (event, scope) => {
    return await actions.inst_evh_1040167445267876_submit_8n20xbfa4.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1040167445267876_submit = async (event, scope) => {
    return await actions.inst_evh_1040167445267876_submit.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2434514588044777_clickPreventStop_6d6j0gyfo = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_2434514588044777_clickPreventStop_6d6j0gyfo.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2434514588044777_clickPreventStop = async (event, scope) => {
    return await actions.inst_evh_2434514588044777_clickPreventStop.executeFromDOM(
      $component,
      event,
      scope
    );
  };

  defineExpose({
    documentTitle,
    pageDocument: _pageDocument,
    routeTab,
    loadData,
    loading,
    loaded,
    error,
  });
</script>

<template>
  <ItpPage
    :header="documentTitle"
    :loaded="loaded"
    :noShow="b_7315092382398562_noShow"
    :loading="showLoadingIndicator"
    :reloading="reloading"
    :error="error"
    :modal="modal"
    :modalBindings="b_7315092382398562_modalBindings"
    itp-cname="CreateMeterReadingsParameters"
    @close="evh_7315092382398562_close($event, {})"
    @reload="evh_7315092382398562_reload($event, {})"
  >
    <template #header>
      <UcHeader
        :card="!modal"
        :modal="modal"
        :title="documentTitle"
        v-if="loaded"
        @close="evh_8564662037462133_close($event, {})"
      >
      </UcHeader>
    </template>
    <UcBody :card="!modal" :modal="modal" v-if="loaded">
      <ItpForm
        name="createOptions"
        @submit="evh_1040167445267876_submit($event, {})"
      >
        <ItpFormGroup
          :label-cols-md="4"
          :content-cols-md="8"
          name="notes"
          :label="pgettext('CreateMeterReadings', 'Notes')"
          :labelAlign="labelAlign"
        >
          <ItpFormTextarea
            name="notes"
            v-model="params.notes"
            :value="params.notes"
            @input="
              !($event && typeof $event.stopPropagation === 'function') &&
              (params.notes =
                $event && typeof $event === 'object' && 'data' in $event
                  ? $event.data
                  : $event)
            "
          >
          </ItpFormTextarea>
        </ItpFormGroup>
        <ItpFormGroup
          :label-cols-md="4"
          :content-cols-md="8"
          name="dateOverride"
          :label="pgettext('CreateMeterReadings', 'Reading date')"
          :description="
            pgettext(
              'CreateMeterReadings',
              'Use this date as reading date instead.'
            )
          "
          :labelAlign="labelAlign"
        >
          <ItpFormDatetime
            name="dateOverride"
            clearable
            v-model="params.date_override"
          >
          </ItpFormDatetime>
        </ItpFormGroup>
      </ItpForm>
    </UcBody>
    <template #footer>
      <UcFooter :card="!modal" :modal="modal" v-if="loaded">
        <ItpButton
          :form="forms.createOptions && forms.createOptions.id"
          :text="pgettext('MeterMeasurementGenerateButton', 'Create Readings')"
          type="submit"
          variant="primary"
          :spinning="actions.submit.isRunning"
        >
        </ItpButton>
        <ItpButton
          :text="pgettext('CreateMeterReadings', 'Cancel')"
          variant="default"
          :disabled="actions.submit.isRunning"
          @click.prevent.stop="
            evh_2434514588044777_clickPreventStop($event, {})
          "
        >
        </ItpButton>
      </UcFooter>
    </template>
  </ItpPage>
</template>
